import useAxios from "../hooks/useAxios";
import { BulletinService } from "./BulletinService";
import { BulletinSectionService } from "./BulletinSectionService";
import { BulletinContentService } from "./BulletinContentService";
import { BulletinEventService } from "./BulletinEventService";

export const useServices = () => {
  const { fetchData, response, error, loading } = useAxios();

  const bulletinService = new BulletinService(fetchData);
  const sectionService = new BulletinSectionService(fetchData);
  const contentService = new BulletinContentService(fetchData);

  return {
    bulletinService,
    sectionService,
    contentService,
    response,
    error,
    loading,
  };
};

// Individual service hooks
export const useBulletinService = () => {
  const { fetchData, response, error, loading } = useAxios();
  return {
    service: new BulletinService(fetchData),
    response,
    error,
    loading,
  };
};

export const useBulletinSectionService = () => {
  const { fetchData, response, error, loading } = useAxios();
  return {
    service: new BulletinSectionService(fetchData),
    response,
    error,
    loading,
  };
};

export const useBulletinContentService = () => {
  const { fetchData, response, error, loading } = useAxios();
  return {
    service: new BulletinContentService(fetchData),
    response,
    error,
    loading,
  };
};

export const useBulletinEventService = () => {
  const { fetchData, response, error, loading } = useAxios();
  return {
    service: new BulletinEventService(fetchData),
    response,
    error,
    loading,
  };
};

// Usage examples:

/*
// Example 1: Using the combined services hook
import { useServices } from '../services';

const MyComponent = () => {
  const { bulletinService, response, error, loading } = useServices();

  useEffect(() => {
    const loadBulletins = async () => {
      await bulletinService.getAllBulletins({ page: 1, limit: 10 });
    };
    loadBulletins();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <div>
      {response?.data?.map(bulletin => (
        <div key={bulletin.id}>{bulletin.title}</div>
      ))}
    </div>
  );
};

// Example 2: Using individual service hooks
import { useBulletinService } from '../services';

const BulletinList = () => {
  const { service, response, error, loading } = useBulletinService();

  const handleCreateBulletin = async (formData) => {
    await service.createBulletin(formData);
  };

  const handleDeleteBulletin = async (id) => {
    await service.deleteBulletin(id);
  };

  return (
    // Component JSX
  );
};

// Example 3: Working with nested resources
import { useServices } from '../services';

const BulletinDetail = ({ bulletinId }) => {
  const { sectionService, contentService, response, error, loading } = useServices();

  const loadSectionsAndContent = async () => {
    // Load sections
    await sectionService.getAllSections(bulletinId);
    
    // Load content for first section (example)
    const sections = response?.data;
    if (sections?.length > 0) {
      await contentService.getAllContent(bulletinId, sections[0].id);
    }
  };

  useEffect(() => {
    loadSectionsAndContent();
  }, [bulletinId]);

  return (
    // Component JSX
  );
};

// Example 4: Form submission with error handling
const CreateBulletinForm = () => {
  const { bulletinService, response, error, loading } = useServices();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    expiry_date: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await bulletinService.createBulletin(formData);
      if (response && !error) {
        // Success - redirect or show success message
        console.log('Bulletin created successfully:', response);
      }
    } catch (err) {
      console.error('Error creating bulletin:', err);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        type="text"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        placeholder="Bulletin Title"
        required
      />
      <textarea
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
        placeholder="Description"
        required
      />
      <input
        type="url"
        value={formData.cover_image}
        onChange={(e) => setFormData({...formData, cover_image: e.target.value})}
        placeholder="Cover Image URL"
      />
      <input
        type="datetime-local"
        value={formData.expiry_date}
        onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
      />
      <button type="submit" disabled={loading}>
        {loading ? 'Creating...' : 'Create Bulletin'}
      </button>
      {error && <div className="error">{error.message}</div>}
    </form>
  );
};
*/
